/**
 * Acquisition health score — the value behind the hero ring gauge.
 *
 * Two equally-weighted (50 / 50) halves, each scored on a 0–1 scale, then blended
 * and mapped to a 0–100 gauge value with a hard floor:
 *
 *   1. Campaign queue (50%) — how many Lumio-suggested campaigns are still sitting
 *      in the Suggestions tab waiting to be activated. The ideal is an empty queue
 *      (nothing left to action → full marks); a backlog of
 *      {@link MAX_QUEUED_SUGGESTIONS} or more scores zero. Linear in between.
 *
 *   2. Budget runway (50%) — how many days the marketing wallet lasts at the
 *      current daily spend limit (`availableBudget / dailySpendLimit`).
 *      {@link FULL_RUNWAY_DAYS}+ days → full marks, {@link HALF_RUNWAY_DAYS} day →
 *      half marks, under a day → ramps down to zero. Piecewise-linear through
 *      those three anchors.
 *
 * The blended result is clamped to never read below {@link MIN_SCORE} (a fully
 * neglected account still shows a non-zero ring) and rounded to a whole number.
 *
 * Pure + framework-free, so it is trivially unit-testable (see
 * `acquisition-score.test.ts`) and survives the mock→API swap unchanged: feed it
 * real wallet / limit / queue figures from the future selector and the gauge keeps
 * working with no edits here.
 */
import { gaugeStatusLabel, type Tone } from '../../theme/tokens';

/** A queue of this many (or more) un-activated suggestions scores zero on the campaign half. */
export const MAX_QUEUED_SUGGESTIONS = 10;
/** Days of runway that earn full marks on the budget half. */
export const FULL_RUNWAY_DAYS = 3;
/** Days of runway that earn half marks on the budget half. */
export const HALF_RUNWAY_DAYS = 1;
/** Each half contributes this fraction of the blended score (they sum to 1). */
export const CAMPAIGN_WEIGHT = 0.5;
export const BUDGET_WEIGHT = 0.5;
/** The gauge never reads below this, however neglected the account. */
export const MIN_SCORE = 10;

export type AcquisitionScoreInputs = {
  /** Campaigns sitting in the Suggestions tab, waiting to be activated. */
  queuedSuggestions: number;
  /** Marketing wallet balance — the spendable acquisition budget. */
  availableBudget: number;
  /** Daily ad-spend cap. Runway (days) = availableBudget / dailySpendLimit. */
  dailySpendLimit: number;
};

export type AcquisitionScoreBreakdown = {
  /** Final 0–100 gauge value (rounded, floored at {@link MIN_SCORE}). */
  score: number;
  /** Campaign-queue half, 0–1 (1 = empty queue). */
  campaignScore: number;
  /** Budget-runway half, 0–1 (1 = {@link FULL_RUNWAY_DAYS}+ days of runway). */
  budgetScore: number;
  /** Days the wallet lasts at the daily limit (Infinity when no limit is set). */
  runwayDays: number;
};

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/**
 * Campaign-queue half. Full marks for an empty queue, sliding linearly to zero at
 * {@link MAX_QUEUED_SUGGESTIONS} queued (and staying there beyond it).
 */
export const campaignQueueScore = (queuedSuggestions: number): number =>
  clamp01(1 - Math.max(0, queuedSuggestions) / MAX_QUEUED_SUGGESTIONS);

/**
 * Budget-runway half, piecewise-linear through the three anchors the spec names:
 * 0 days → 0, {@link HALF_RUNWAY_DAYS} day → 0.5, {@link FULL_RUNWAY_DAYS}+ days →
 * 1. Under a day the points ramp down toward zero ("budget for under a day → no
 * points"); a day buys half; three days or more is full. An absent spend limit
 * (Infinity runway) reads as full — the account is not budget-constrained.
 */
export const budgetRunwayScore = (runwayDays: number): number => {
  if (runwayDays >= FULL_RUNWAY_DAYS) return 1; // 3+ days (incl. Infinity) → full marks
  if (!(runwayDays > 0)) return 0; // 0, negative or NaN → no marks
  if (runwayDays >= HALF_RUNWAY_DAYS) {
    // 0.5 at HALF_RUNWAY_DAYS day → 1 at FULL_RUNWAY_DAYS days.
    return 0.5 + 0.5 * ((runwayDays - HALF_RUNWAY_DAYS) / (FULL_RUNWAY_DAYS - HALF_RUNWAY_DAYS));
  }
  // 0 at 0 days → 0.5 at HALF_RUNWAY_DAYS day.
  return 0.5 * (runwayDays / HALF_RUNWAY_DAYS);
};

/**
 * Blend the two halves into the 0–100 gauge value. The campaign and budget scores
 * are returned alongside it (and the derived runway) so the caller can explain the
 * number — e.g. surface which lever is dragging the score down.
 */
export const computeAcquisitionScore = (inputs: AcquisitionScoreInputs): AcquisitionScoreBreakdown => {
  const { queuedSuggestions, availableBudget, dailySpendLimit } = inputs;
  // No daily cap → not budget-limited (Infinity runway), unless there is also no
  // wallet at all, which is genuinely zero runway.
  const runwayDays =
    dailySpendLimit > 0 ? Math.max(0, availableBudget) / dailySpendLimit : availableBudget > 0 ? Infinity : 0;

  const campaignScore = campaignQueueScore(queuedSuggestions);
  const budgetScore = budgetRunwayScore(runwayDays);
  const blended = CAMPAIGN_WEIGHT * campaignScore + BUDGET_WEIGHT * budgetScore;
  const score = Math.max(MIN_SCORE, Math.round(blended * 100));

  return { score, campaignScore, budgetScore, runwayDays };
};

export type AcquisitionSummaryDerived = {
  /** Gauge-centre band label. */
  status: string;
  /** Tone token for the score band. */
  tone: Tone;
  /** Hero headline copy for the band (English-as-key, passed through `<Text>`). */
  title: string;
};

/**
 * Score → band label / tone / headline. The label comes from the shared
 * {@link gaugeStatusLabel} (so the Acquisition gauge and the Home gauges read the
 * same word for the same score); this only adds the acquisition-specific tone and
 * hero headline copy per band.
 */
export const acquisitionScoreSummary = (score: number): AcquisitionSummaryDerived => {
  const status = gaugeStatusLabel(score);
  if (status === 'Optimal') return { status, tone: 'optimal', title: 'Player Acquisition is on track' };
  if (status === 'Sustainable') return { status, tone: 'sustainable', title: 'Player Acquisition is doing well' };
  return { status, tone: 'poor', title: 'Player Acquisition needs attention' };
};

/**
 * A half (0–1) at or below this — scaled to 0–100 — reads as "Poor", the same cut
 * the overall gauge uses (red ≤ 60). When a half is Poor it is "lacking" and its
 * hero recommendation is surfaced.
 */
export const HALF_LACKING_AT = 60;

/** The two hero recommendations, each tied to the half it remedies. */
export type AcquisitionActionId = 'increase-daily-budget' | 'activate-new-campaign';

const isLacking = (halfScore: number): boolean => halfScore * 100 <= HALF_LACKING_AT;

/**
 * Which hero recommendation(s) to surface, decided by which half of the score is
 * lacking (in the "Poor" band, ≤ {@link HALF_LACKING_AT} on its 0–100 scale):
 *
 *   - budget half Poor   → "Increase daily budget"
 *   - campaign half Poor → "Activate a new campaign"
 *   - both halves good   → [] (nothing to action — the card hides the suggestions)
 *
 * Note this keys off each HALF, not the blended score: a brand can sit in the
 * overall "Sustainable" band yet still surface one action because one half alone
 * is Poor. Budget is listed first to preserve the card's existing order.
 *
 * The campaign half is fed the REAL queued-suggestion count (see
 * {@link computeAcquisitionScore}), so "Activate a new campaign" only surfaces when
 * there genuinely is a backlog of suggested campaigns to action — an empty queue
 * scores full marks and surfaces nothing.
 */
export const acquisitionActions = (breakdown: AcquisitionScoreBreakdown): AcquisitionActionId[] => {
  const actions: AcquisitionActionId[] = [];
  if (isLacking(breakdown.budgetScore)) actions.push('increase-daily-budget');
  if (isLacking(breakdown.campaignScore)) actions.push('activate-new-campaign');
  return actions;
};

/**
 * Hero card heading — a concise, state-aware sentence that names what (if
 * anything) is holding acquisition back, driven by the same lacking-half logic as
 * the suggestion buttons. Deliberately short (5–6 words) to fit the card's
 * two-line heading; each line names the actual lever so the heading is more
 * informative than a generic band phrase.
 *
 * The campaign lever is fed the REAL queued-suggestion count, so "has campaigns to
 * activate" only reads when there genuinely is a backlog; an empty queue scores
 * full marks and falls through to the budget lever or "on track".
 */
export const acquisitionHeading = (breakdown: AcquisitionScoreBreakdown): string => {
  const budgetLow = isLacking(breakdown.budgetScore);
  const queueHigh = isLacking(breakdown.campaignScore);
  if (budgetLow && queueHigh) return 'Player Acquisition needs budget and campaigns';
  if (budgetLow) return 'Player Acquisition is low on budget';
  if (queueHigh) return 'Player Acquisition has campaigns to activate';
  return 'Player Acquisition is on track';
};
